import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layers, Brain, BarChart3, Wrench, Sparkles, TrendingUp, Target, Activity, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEngine } from '../hooks/useEngine.js';
import { next as baseNext } from '../data/history.js';
import { useHistoryStore } from '../stores/history.js';
import { fetchLatestData } from '../lib/dataFetcher.js';
import { api } from '../lib/api.js';

const features = [
  { to: '/pool', icon: Layers, title: '4 层大底池', desc: '1000 / 500 / 300 / 100 层层筛选,子集关系保证', color: 'from-brand-purple to-brand-cyan' },
  { to: '/pick', icon: Brain, title: '智能选号 v2', desc: '多策略权重 + 复式自动算 + 实时命中率', color: 'from-brand-rose to-brand-gold' },
  { to: '/trend', icon: BarChart3, title: '数据可视化', desc: '走势图 + 热号 + 跨度/和值/形态分布', color: 'from-brand-cyan to-brand-lime' },
  { to: '/tools', icon: Wrench, title: '工具集', desc: '缩水器 + 矩阵 + 组号器 + 数据导出', color: 'from-brand-gold to-brand-rose' },
];

export default function Dashboard() {
  const { result, killPool, danPool, heatMap, pools } = useEngine();
  const history = useHistoryStore(s => s.history);
  const updatedAt = useHistoryStore(s => s.updatedAt);
  const refresh = useHistoryStore(s => s.refresh);
  const refreshing = useHistoryStore(s => s.refreshing);
  const training = useHistoryStore(s => s.training);
  const trainingProgress = useHistoryStore(s => s.trainingProgress);
  const last = history[0];

  // Dashboard 启动时从后端拉最新 3D 数据(MAX v7)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('https://api.3dmfkj.top/api/3d/history?N=120').then(r => r.json());
        if (cancelled || !r?.ok || !r.data || r.data.length === 0) return;
        // 转换格式(同时保留 s/sp 短名 和 sum/span/t 全名,兼容公式和 UI)
        const fresh = r.data.map(x => ({
          p: x.p,
          date: x.created_at || x.date,
          a: x.a, b: x.b, c: x.c,
          sum: x.sum, span: x.span, type: x.type,
          s: x.sum, sp: x.span, t: x.type,
        }));
        const cur = useHistoryStore.getState().history;
        const merged = [...fresh, ...cur].reduce((acc, x) => {
          if (!acc.find(y => y.p === x.p)) acc.push(x);
          return acc;
        }, []).sort((a, b) => parseInt(b.p) - parseInt(a.p));
        useHistoryStore.setState({ history: merged, updatedAt: Date.now() });
        console.log(`[Dashboard 3D] 同步后端历史: ${fresh.length} 期,最新 ${fresh[0]?.p}`);
      } catch (e) {
        console.warn('[Dashboard 3D] 拉后端历史失败:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 客户端实时更新
  const handleRefresh = async () => {
    const tid = toast.loading('正在拉取最新开奖数据...');
    try {
      const res = await refresh(fetchLatestData);
      if (res?.latest) {
        toast.success(`已更新到 ${res.latest.p} 期 (${res.latest.d})`, { id: tid });
      } else {
        toast.success('数据已更新', { id: tid });
      }
    } catch (e) {
      console.error(e);
      toast.error('更新失败:' + e.message, { id: tid });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card relative overflow-hidden p-4 sm:p-6"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-brand-purple/20 blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-brand-cyan/20 blur-3xl"></div>
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-brand-gold/10 border border-brand-gold/30 text-[10px] sm:text-xs mb-2 sm:mb-3">
            <Sparkles size={10} className="text-brand-gold" />
            <span className="text-brand-gold font-semibold">3D MAX · MAX 版本</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold mb-2 sm:mb-3 leading-tight">
            <span className="gradient-text">智能杀号 · 大底池</span>
            <br />
            <span className="text-ink-0">专业版系统</span>
          </h1>
          <p className="text-ink-2 text-sm sm:text-base max-w-2xl mb-4 sm:mb-6">
            智能杀号 + 4 层大底池 + 多策略选号 + 数据可视化,专业级彩票分析工具。
          </p>

          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Link to="/pool" className="btn-primary flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base">
              <Layers size={14} /> 进入 4 层大底池
            </Link>
            <Link to="/pick" className="btn-ghost flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base">
              <Brain size={14} /> 智能选号 v2
            </Link>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-ghost flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base disabled:opacity-50"
              title="从 ssqzj.com 拉取最新数据"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? '更新中...' : '立即更新'}
            </button>
          </div>
          {updatedAt && (
            <div className="text-[10px] text-ink-3 mt-2">
              最后更新:{new Date(updatedAt).toLocaleString('zh-CN')} · 数据源 ssqzj.com
            </div>
          )}
          {training && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-cyan/10 border border-brand-cyan/30 text-[10px] text-brand-cyan">
              <RefreshCw size={10} className="animate-spin" /> ML 在线训练中...{trainingProgress}
            </div>
          )}
        </div>
      </motion.section>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        {[
          { label: '目标期', value: last.p ? String(parseInt(last.p) + 1) : baseNext.period, color: 'text-brand-cyan' },
          { label: `上期 ${last.p || '----'}`, value: last.a != null ? `${last.a}${last.b}${last.c}` : '---', color: 'text-brand-gold' },
          { label: '杀号公式', value: '15+', color: 'text-brand-rose' },
          { label: '历史数据', value: `${history.length} 期`, color: 'text-brand-purple' },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="card p-3 sm:p-4"
          >
            <div className="text-[10px] sm:text-xs text-ink-3 mb-1">{s.label}</div>
            <div className={`text-base sm:text-xl font-bold font-mono ${s.color}`}>{s.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Quick kill analysis */}
      <div className="card p-4 sm:p-5">
        <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
          <Target size={16} className="text-brand-rose" />
          本期核心杀号
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <div className="text-xs text-ink-3 mb-2">强杀号(axis + 上期十位)</div>
            <div className="flex gap-1.5 flex-wrap">
              {pools.kills.axisNums.map(n => (
                <span key={n} className="chip chip-dan">{n}</span>
              ))}
              {pools.kills.shiqiweiKill.map(n => (
                <span key={n} className="chip chip-pair">{n}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-2">杀跨度 / 杀和尾</div>
            <div className="flex gap-1.5 flex-wrap">
              {pools.kills.killKuaDu.map(n => (
                <span key={n} className="chip">{n}</span>
              ))}
              <span className="text-ink-3">·</span>
              {pools.kills.killHeWei.map(n => (
                <span key={n} className="chip">{n}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-2">主胆 / 全部胆码</div>
            <div className="flex gap-1.5 flex-wrap">
              <span className="chip chip-dan font-bold">{pools.mainDan}</span>
              {pools.allDans.slice(1).map(n => (
                <span key={n} className="chip">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 4 pool overview */}
      <div className="card p-4 sm:p-5">
        <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
          <Activity size={16} className="text-brand-cyan" />
          4 层大底池速览
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
          {[
            { label: 'L1 基础池', count: pools.pool1000.length, raw: pools.stats.L1_raw, color: 'from-brand-purple/20 to-brand-cyan/20' },
            { label: 'L2 500', count: pools.pool500.length, raw: pools.stats.L2_raw, color: 'from-brand-cyan/20 to-brand-lime/20' },
            { label: 'L3 300', count: pools.pool300.length, raw: pools.stats.L3_raw, color: 'from-brand-gold/20 to-brand-rose/20' },
            { label: 'L4 100', count: pools.pool100.length, raw: pools.stats.L4_raw, color: 'from-brand-rose/20 to-brand-purple/20' },
          ].map((p, i) => (
            <Link
              key={p.label}
              to="/pool"
              className={`card-hover bg-gradient-to-br ${p.color} border border-white/10 p-3 sm:p-4 rounded-xl block`}
            >
              <div className="text-[10px] sm:text-xs text-ink-3 mb-1">{p.label}</div>
              <div className="text-xl sm:text-2xl font-bold gradient-text">{p.count}</div>
              <div className="text-[9px] sm:text-[10px] text-ink-3 mt-1">候选 {p.raw}</div>
            </Link>
          ))}
        </div>
        <div className="mt-3 text-[10px] sm:text-xs text-ink-3 flex items-center flex-wrap gap-1.5 sm:gap-2">
          <span>子集关系:</span>
          <span className={pools.stats.L2_in_L1 ? 'text-accent-ok' : 'text-accent-danger'}>
            L2⊂L1 {pools.stats.L2_in_L1 ? '✓' : '✗'}
          </span>
          <span>·</span>
          <span className={pools.stats.L3_in_L2 ? 'text-accent-ok' : 'text-accent-danger'}>
            L3⊂L2 {pools.stats.L3_in_L2 ? '✓' : '✗'}
          </span>
          <span>·</span>
          <span className={pools.stats.L4_in_L3 ? 'text-accent-ok' : 'text-accent-danger'}>
            L4⊂L3 {pools.stats.L4_in_L3 ? '✓' : '✗'}
          </span>
        </div>
      </div>

      {/* Features grid */}
      <div>
        <h2 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-brand-gold" />
          MAX 版本功能矩阵
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {features.map((f, i) => (
            <motion.div
              key={f.to}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Link
                to={f.to}
                className="card card-hover flex items-start gap-3 sm:gap-4 group p-3 sm:p-5"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <f.icon size={18} className="text-bg-base" />
                </div>
                <div>
                  <div className="font-bold text-sm sm:text-base text-ink-0 group-hover:text-brand-cyan transition-colors">{f.title}</div>
                  <div className="text-xs sm:text-sm text-ink-3 mt-0.5 sm:mt-1">{f.desc}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
